<template>
  <div class="card mb-4">
    <div class="card-header pb-0">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <button class="btn btn-sm btn-outline-secondary me-2" @click="$router.back()">
            <i class="fas fa-arrow-left me-1"></i>
            Voltar
          </button>
          <h6 class="d-inline-block mb-0 ms-1">Produtos do Folheto #{{ flyerId }}</h6>
        </div>
        <div class="d-flex gap-2">
          <button
            @click="openAddProductModal"
            class="btn btn-sm btn-custom-gradient"
          >
            <i class="fas fa-plus me-1"></i>
            Adicionar Produto
          </button>
          <button
            @click="loadProducts"
            class="btn btn-sm"
            :class="loading ? 'btn-secondary' : 'btn-custom-gradient'"
            :disabled="loading"
          >
            {{ loading ? 'Carregando...' : 'Atualizar' }}
          </button>
        </div>
      </div>
      <p class="text-sm mb-0 mt-2">
        <span class="font-weight-bold">{{ total }}</span> produtos identificados neste folheto
      </p>
    </div>
    <div class="card-body">
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border spinner-teal" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="products.length === 0" class="text-center py-4">
          <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
          <p class="text-muted">Nenhum produto encontrado para este folheto</p>
        </div>
        <div v-else class="products-grid">
          <div
            v-for="product in products"
            :key="product.id"
            class="product-card"
          >
            <div
              class="product-thumb"
              :class="{ 'product-thumb--clickable': getImageUrl(product) }"
              @click="getImageUrl(product) && openImageLightbox(getImageUrl(product))"
              title="Ver imagem ampliada"
            >
              <img
                v-if="getImageUrl(product)"
                :src="getImageUrl(product)"
                :alt="product.name"
                @error="$event.target.style.display = 'none'"
              />
              <i v-else class="fas fa-box-open product-no-img"></i>
              <span v-if="product.home" class="product-home-badge" title="Em destaque na Home">
                <i class="fas fa-star"></i>
                Home
              </span>
              <button
                class="product-edit-image-btn"
                type="button"
                title="Editar imagem"
                @click.stop="openEditImageModal(product)"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
              <button
                class="product-delete-btn"
                type="button"
                title="Excluir produto"
                :disabled="deletingIds.has(product.id)"
                @click.stop="deleteProduct(product)"
              >
                <i class="fas" :class="deletingIds.has(product.id) ? 'fa-circle-notch fa-spin' : 'fa-trash-alt'"></i>
              </button>
            </div>
            <div class="product-info">
              <p class="product-name">{{ product.name || 'Sem nome' }}</p>
              <div class="product-meta">
                <span>{{ product.quantity }} {{ product.unit }}</span>
                <soft-badge :color="getStatusColor(product.status)" variant="gradient" size="sm">
                  {{ getStatusText(product.status) }}
                </soft-badge>
              </div>
              <div class="product-price-row">
                <p class="product-price">R$ {{ formatPrice(product.price) }}</p>
                <button
                  class="product-edit-link-btn"
                  type="button"
                  title="Editar preço"
                  @click="openEditPriceModal(product)"
                >
                  <i class="fas fa-tag"></i>
                  Preço
                </button>
              </div>
              <button
                class="product-edit-link-btn product-edit-link-btn--full"
                type="button"
                title="Editar dados do produto"
                @click="openEditProductModal(product)"
              >
                <i class="fas fa-edit"></i>
                Editar descrição
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox de imagem ampliada -->
    <div v-if="showImageLightbox" class="modal-backdrop fade show" @click="closeImageLightbox" style="z-index: 10999;"></div>
    <div v-if="showImageLightbox" class="image-lightbox" @click="closeImageLightbox" style="z-index: 11000;">
      <button type="button" class="image-lightbox-close" @click.stop="closeImageLightbox" aria-label="Close">
        <i class="fas fa-times"></i>
      </button>
      <img :src="lightboxImageUrl" alt="Produto ampliado" class="image-lightbox-img" @click.stop />
    </div>

    <!-- Modal de Editar Imagem -->
    <div v-if="showEditImageModal" class="modal-backdrop fade show" @click="closeEditImageModal" style="z-index: 9999;"></div>
    <div v-if="showEditImageModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="editFlyerProductImageModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editFlyerProductImageModalLabel">
              <i class="fas fa-image me-2"></i>
              Editar Imagem - {{ editingProduct?.name || '' }}
            </h5>
            <button type="button" class="btn-close" @click="closeEditImageModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div
              class="edit-image-upload-zone"
              :class="{ 'has-file': editImageFile }"
              @click="triggerEditImageInput"
              @dragover.prevent="editImageDragging = true"
              @dragleave.prevent="editImageDragging = false"
              @drop.prevent="handleEditImageDrop"
              :style="editImageDragging ? 'border-color:#17c1e8;' : ''"
            >
              <template v-if="editImagePreviewUrl">
                <img :src="editImagePreviewUrl" alt="Preview" class="edit-image-preview" />
              </template>
              <template v-else>
                <div class="upload-placeholder">
                  <i class="fas fa-cloud-upload-alt mb-2"></i>
                  <p class="mb-0 text-sm">Arraste ou clique para selecionar a nova imagem</p>
                </div>
              </template>
            </div>
            <input ref="editImageInput" type="file" accept="image/*" class="d-none" @change="handleEditImageFileChange" />
            <div v-if="editImageError" class="alert alert-danger py-2 mt-3 mb-0">
              {{ editImageError }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditImageModal" :disabled="savingImage">Cancelar</button>
            <button
              type="button"
              class="btn btn-custom-gradient"
              @click="saveProductImage"
              :disabled="!editImageFile || savingImage"
            >
              <span v-if="savingImage">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                Enviando...
              </span>
              <span v-else>
                <i class="fas fa-save me-1"></i>
                Salvar
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Editar Preço -->
    <div v-if="showEditPriceModal" class="modal-backdrop fade show" @click="closeEditPriceModal" style="z-index: 9999;"></div>
    <div v-if="showEditPriceModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="editFlyerProductPriceModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editFlyerProductPriceModalLabel">
              <i class="fas fa-tag me-2"></i>
              Editar Preço - {{ editingPriceProduct?.name || '' }}
            </h5>
            <button type="button" class="btn-close" @click="closeEditPriceModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveProductPrice">
              <div class="mb-3">
                <label class="form-label">Preço (R$) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  v-model="editPriceForm.price"
                  placeholder="Ex: 25.99"
                >
              </div>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="editPriceHomeCheck"
                  v-model="editPriceForm.home"
                >
                <label class="form-check-label" for="editPriceHomeCheck">
                  <i class="fas fa-star text-warning me-1"></i>
                  Destacar na Home do app
                </label>
              </div>
            </form>
            <div v-if="editPriceError" class="alert alert-danger py-2 mt-3 mb-0">
              {{ editPriceError }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditPriceModal" :disabled="savingPrice">Cancelar</button>
            <button
              type="button"
              class="btn btn-custom-gradient"
              @click="saveProductPrice"
              :disabled="!editPriceForm.price || parseFloat(editPriceForm.price) <= 0 || savingPrice"
            >
              <span v-if="savingPrice">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                Salvando...
              </span>
              <span v-else>
                <i class="fas fa-save me-1"></i>
                Salvar
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Editar Descrição -->
    <div v-if="showEditProductModal" class="modal-backdrop fade show" @click="closeEditProductModal" style="z-index: 9999;"></div>
    <div v-if="showEditProductModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="editFlyerProductModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editFlyerProductModalLabel">
              <i class="fas fa-edit me-2"></i>
              Editar Produto
            </h5>
            <button type="button" class="btn-close" @click="closeEditProductModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveProductData">
              <div class="mb-3">
                <label class="form-label">Nome *</label>
                <input type="text" class="form-control" v-model="editProductForm.name" placeholder="Nome do produto">
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label class="form-label">Quantidade</label>
                  <input type="text" class="form-control" v-model="editProductForm.quantity" placeholder="Ex: 1.000">
                </div>
                <div class="col-6 mb-3">
                  <label class="form-label">Unidade</label>
                  <input type="text" class="form-control" v-model="editProductForm.unit" placeholder="Ex: kg">
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Marca</label>
                <input type="text" class="form-control" v-model="editProductForm.brand" placeholder="Ex: Friboi">
              </div>
            </form>
            <div v-if="editProductError" class="alert alert-danger py-2 mb-0">
              {{ editProductError }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditProductModal" :disabled="savingProductData">Cancelar</button>
            <button
              type="button"
              class="btn btn-custom-gradient"
              @click="saveProductData"
              :disabled="!editProductForm.name || savingProductData"
            >
              <span v-if="savingProductData">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                Salvando...
              </span>
              <span v-else>
                <i class="fas fa-save me-1"></i>
                Salvar
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Adicionar Produto -->
    <div v-if="showAddProductModal" class="modal-backdrop fade show" @click="closeAddProductModal" style="z-index: 9999;"></div>
    <div v-if="showAddProductModal" class="modal fade show d-block" tabindex="-1" aria-labelledby="addFlyerProductModalLabel" aria-hidden="true" style="z-index: 10000;">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addFlyerProductModalLabel">
              <i class="fas fa-plus me-2"></i>
              Adicionar Produto ao Folheto
            </h5>
            <button type="button" class="btn-close" @click="closeAddProductModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitAddProduct">
              <div class="mb-3">
                <label class="form-label">Nome *</label>
                <input type="text" class="form-control" v-model="addProductForm.name" placeholder="Nome do produto">
              </div>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Marca</label>
                  <input type="text" class="form-control" v-model="addProductForm.brand" placeholder="Ex: Friboi">
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Quantidade</label>
                  <input type="text" class="form-control" v-model="addProductForm.quantity" placeholder="Ex: 1.000">
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Unidade</label>
                  <input type="text" class="form-control" v-model="addProductForm.unit" placeholder="Ex: kg">
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Preço (R$) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  v-model="addProductForm.price"
                  placeholder="Ex: 23.99"
                >
              </div>
              <div class="form-check mb-3">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="addProductHomeCheck"
                  v-model="addProductForm.home"
                >
                <label class="form-check-label" for="addProductHomeCheck">
                  <i class="fas fa-star text-warning me-1"></i>
                  Destacar na Home do app
                </label>
              </div>
              <div class="mb-3">
                <label class="form-label">Imagem</label>
                <div
                  class="edit-image-upload-zone"
                  :class="{ 'has-file': addProductImageFile }"
                  @click="triggerAddProductImageInput"
                  @dragover.prevent="addProductImageDragging = true"
                  @dragleave.prevent="addProductImageDragging = false"
                  @drop.prevent="handleAddProductImageDrop"
                  :style="addProductImageDragging ? 'border-color:#17c1e8;' : ''"
                >
                  <template v-if="addProductImagePreviewUrl">
                    <img :src="addProductImagePreviewUrl" alt="Preview" class="edit-image-preview" />
                  </template>
                  <template v-else>
                    <div class="upload-placeholder">
                      <i class="fas fa-cloud-upload-alt mb-2"></i>
                      <p class="mb-0 text-sm">Arraste ou clique para selecionar (opcional)</p>
                    </div>
                  </template>
                </div>
                <input ref="addProductImageInput" type="file" accept="image/*" class="d-none" @change="handleAddProductImageFileChange">
              </div>
              <div class="mb-2">
                <label class="form-label d-block">Supermercados *</label>
                <div v-if="loadingSupermercados" class="d-flex align-items-center gap-2">
                  <div class="spinner-border spinner-border-sm spinner-teal"></div>
                  <span class="text-sm text-secondary">Carregando...</span>
                </div>
                <div v-else-if="supermercados.length === 0" class="text-secondary text-sm">
                  Nenhum supermercado encontrado
                </div>
                <div v-else class="supermarket-select-list">
                  <label
                    v-for="s in supermercados"
                    :key="s.id"
                    class="supermarket-select-row"
                    :class="{ 'supermarket-select-row--selected': addProductSupermarketIds.has(s.id) }"
                  >
                    <input
                      type="checkbox"
                      class="supermarket-select-checkbox"
                      :checked="addProductSupermarketIds.has(s.id)"
                      @change="toggleAddProductSupermarket(s.id)"
                    >
                    <span>{{ s.nome || s.name }}</span>
                  </label>
                </div>
              </div>
            </form>
            <div v-if="addProductError" class="alert alert-danger py-2 mt-2 mb-0">
              {{ addProductError }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddProductModal" :disabled="savingAddProduct">Cancelar</button>
            <button
              type="button"
              class="btn btn-custom-gradient"
              @click="submitAddProduct"
              :disabled="!canSubmitAddProduct || savingAddProduct"
            >
              <span v-if="savingAddProduct">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                Salvando...
              </span>
              <span v-else>
                <i class="fas fa-save me-1"></i>
                Salvar
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";
import { API_ENDPOINTS, apiRequest, handleApiResponse, STORAGE_BASE_URL } from "@/config/api.js";
import { showSuccessAlert, showErrorAlert, showConfirmAlert } from "@/utils/swal.js";

export default {
  name: "flyer-produtos-cards",
  components: {
    SoftBadge,
  },
  data() {
    return {
      products: [],
      total: 0,
      loading: false,
      error: null,
      deletingIds: new Set(),
      showImageLightbox: false,
      lightboxImageUrl: null,

      showEditImageModal: false,
      editingProduct: null,
      editImageFile: null,
      editImagePreviewUrl: null,
      editImageDragging: false,
      savingImage: false,
      editImageError: null,

      showEditPriceModal: false,
      editingPriceProduct: null,
      editPriceForm: { price: "", home: false },
      savingPrice: false,
      editPriceError: null,

      showEditProductModal: false,
      editingProductData: null,
      editProductForm: {
        name: "",
        quantity: "",
        unit: "",
        brand: "",
      },
      savingProductData: false,
      editProductError: null,

      showAddProductModal: false,
      addProductForm: {
        name: "",
        brand: "",
        quantity: "",
        unit: "",
        price: "",
        home: false,
      },
      addProductImageFile: null,
      addProductImagePreviewUrl: null,
      addProductImageDragging: false,
      supermercados: [],
      loadingSupermercados: false,
      addProductSupermarketIds: new Set(),
      savingAddProduct: false,
      addProductError: null,
    };
  },
  computed: {
    flyerId() {
      return this.$route.params.id;
    },
    canSubmitAddProduct() {
      const price = parseFloat(this.addProductForm.price);
      return (
        this.addProductForm.name.trim() !== "" &&
        !isNaN(price) &&
        price > 0 &&
        this.addProductSupermarketIds.size > 0
      );
    },
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    async loadProducts() {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiRequest(API_ENDPOINTS.FLYER_PRODUCTS_BY_FLYER(this.flyerId), {
          method: "GET",
        });

        const data = await handleApiResponse(response);
        const allProducts = Array.isArray(data) ? data : (data.products || data.data || []);
        this.products = allProducts.filter((product) => product.status !== false);
        this.total = this.products.length;
      } catch (error) {
        console.error("Erro ao carregar produtos do folheto:", error);
        this.error = error.message || "Erro ao carregar produtos do folheto";
      } finally {
        this.loading = false;
      }
    },

    getImageUrl(product) {
      const path = product.image_path || product.image;
      if (!path) return null;
      if (/^https?:\/\//i.test(path)) return path;
      return `${STORAGE_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
    },

    openImageLightbox(url) {
      this.lightboxImageUrl = url;
      this.showImageLightbox = true;
    },

    closeImageLightbox() {
      this.showImageLightbox = false;
      this.lightboxImageUrl = null;
    },

    getStatusColor(status) {
      if (status === true || status === "ativo" || status === "active") {
        return "success";
      } else if (status === false || status === "inativo" || status === "inactive") {
        return "secondary";
      }
      return "info";
    },

    getStatusText(status) {
      if (status === true || status === "ativo" || status === "active") {
        return "Ativo";
      } else if (status === false || status === "inativo" || status === "inactive") {
        return "Inativo";
      }
      return "Desconhecido";
    },

    formatPrice(price) {
      if (!price && price !== 0) return "0,00";
      const numPrice = typeof price === "string" ? parseFloat(price.replace(",", ".")) : parseFloat(price);
      if (isNaN(numPrice)) return "0,00";
      return numPrice.toFixed(2).replace(".", ",");
    },

    getProductId(product) {
      return product.id_product ?? product.id;
    },

    async deleteProduct(product) {
      const result = await showConfirmAlert(
        "Excluir Produto",
        `Tem certeza que deseja excluir "${product.name || 'este produto'}" deste folheto?`,
        "Sim, excluir",
        "Cancelar"
      );
      if (!result.isConfirmed) return;

      this.deletingIds.add(product.id);

      try {
        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.FLYER_PRODUCT_PRICE_DEACTIVATE(product.id), {
            method: "PATCH",
          })
        );

        this.products = this.products.filter((p) => p.id !== product.id);
        this.total = Math.max(0, this.total - 1);
        await showSuccessAlert("Produto excluído!", "O produto foi removido deste folheto.", 3000);
      } catch (error) {
        console.error("Erro ao excluir produto:", error);
        showErrorAlert("Erro ao excluir produto", error.message || "Erro ao excluir produto");
      } finally {
        this.deletingIds.delete(product.id);
      }
    },

    // ── Editar imagem ──
    openEditImageModal(product) {
      this.editingProduct = product;
      this.editImageFile = null;
      this.editImagePreviewUrl = null;
      this.editImageError = null;
      this.showEditImageModal = true;
    },

    closeEditImageModal() {
      if (this.savingImage) return;
      this.showEditImageModal = false;
      this.editingProduct = null;
      if (this.editImagePreviewUrl) URL.revokeObjectURL(this.editImagePreviewUrl);
      this.editImagePreviewUrl = null;
      this.editImageFile = null;
      this.editImageError = null;
    },

    triggerEditImageInput() {
      this.$refs.editImageInput.click();
    },

    handleEditImageFileChange(event) {
      const file = event.target.files[0];
      if (file) this.setEditImageFile(file);
    },

    handleEditImageDrop(event) {
      this.editImageDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) this.setEditImageFile(file);
    },

    setEditImageFile(file) {
      if (this.editImagePreviewUrl) URL.revokeObjectURL(this.editImagePreviewUrl);
      this.editImageFile = file;
      this.editImagePreviewUrl = URL.createObjectURL(file);
      this.editImageError = null;
    },

    async saveProductImage() {
      if (!this.editImageFile || !this.editingProduct) return;

      this.savingImage = true;
      this.editImageError = null;

      try {
        const formData = new FormData();
        formData.append("image", this.editImageFile);
        formData.append("_method", "PUT");

        const response = await apiRequest(
          API_ENDPOINTS.FLYER_PRODUCT_IMAGE(this.getProductId(this.editingProduct)),
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await handleApiResponse(response);

        const newPath = data?.image_path || data?.image || data?.image_url;
        if (newPath) {
          this.editingProduct.image = newPath;
        }

        this.closeEditImageModal();
        await showSuccessAlert("Imagem atualizada!", "A imagem do produto foi atualizada com sucesso.", 3000);
      } catch (error) {
        console.error("Erro ao atualizar imagem do produto:", error);
        this.editImageError = error.message || "Erro ao atualizar imagem do produto";
        showErrorAlert("Erro ao atualizar imagem", this.editImageError);
      } finally {
        this.savingImage = false;
      }
    },

    // ── Editar preço ──
    openEditPriceModal(product) {
      this.editingPriceProduct = product;
      this.editPriceForm = {
        price: product.price ? String(product.price) : "",
        home: !!product.home,
      };
      this.editPriceError = null;
      this.showEditPriceModal = true;
    },

    closeEditPriceModal() {
      if (this.savingPrice) return;
      this.showEditPriceModal = false;
      this.editingPriceProduct = null;
      this.editPriceError = null;
    },

    async saveProductPrice() {
      if (!this.editingPriceProduct) return;

      const price = parseFloat(this.editPriceForm.price);
      if (isNaN(price) || price <= 0) {
        this.editPriceError = "Informe um preço válido maior que zero";
        return;
      }

      this.savingPrice = true;
      this.editPriceError = null;

      try {
        const response = await apiRequest(
          API_ENDPOINTS.FLYER_PRODUCT_PRICE_UPDATE(this.editingPriceProduct.id),
          {
            method: "PUT",
            body: JSON.stringify({ price, home: this.editPriceForm.home }),
          }
        );

        const data = await handleApiResponse(response);
        this.editingPriceProduct.price = data?.price ?? price;
        this.editingPriceProduct.home = data?.home ?? this.editPriceForm.home;

        this.showEditPriceModal = false;
        this.editingPriceProduct = null;
        await showSuccessAlert("Preço atualizado!", "O preço do produto foi atualizado com sucesso.", 3000);
      } catch (error) {
        console.error("Erro ao atualizar preço:", error);
        this.editPriceError = error.message || "Erro ao atualizar preço";
      } finally {
        this.savingPrice = false;
      }
    },

    // ── Editar descrição ──
    openEditProductModal(product) {
      this.editingProductData = product;
      this.editProductForm = {
        name: product.name || "",
        quantity: product.quantity || "",
        unit: product.unit || "",
        brand: product.brand || "",
      };
      this.editProductError = null;
      this.showEditProductModal = true;
    },

    closeEditProductModal() {
      if (this.savingProductData) return;
      this.showEditProductModal = false;
      this.editingProductData = null;
      this.editProductError = null;
    },

    async saveProductData() {
      if (!this.editingProductData || !this.editProductForm.name) return;

      this.savingProductData = true;
      this.editProductError = null;

      try {
        const response = await apiRequest(
          API_ENDPOINTS.FLYER_PRODUCT_UPDATE(this.getProductId(this.editingProductData)),
          {
            method: "PUT",
            body: JSON.stringify({
              name: this.editProductForm.name,
              quantity: this.editProductForm.quantity,
              unit: this.editProductForm.unit,
              brand: this.editProductForm.brand,
            }),
          }
        );

        const data = await handleApiResponse(response);

        Object.assign(this.editingProductData, {
          name: data?.name ?? this.editProductForm.name,
          quantity: data?.quantity ?? this.editProductForm.quantity,
          unit: data?.unit ?? this.editProductForm.unit,
          brand: data?.brand ?? this.editProductForm.brand,
        });

        this.showEditProductModal = false;
        this.editingProductData = null;
        await showSuccessAlert("Produto atualizado!", "Os dados do produto foram atualizados com sucesso.", 3000);
      } catch (error) {
        console.error("Erro ao atualizar produto:", error);
        this.editProductError = error.message || "Erro ao atualizar produto";
      } finally {
        this.savingProductData = false;
      }
    },

    // ── Adicionar produto ──
    async openAddProductModal() {
      this.addProductForm = { name: "", brand: "", quantity: "", unit: "", price: "", home: false };
      this.addProductImageFile = null;
      this.addProductImagePreviewUrl = null;
      this.addProductSupermarketIds = new Set();
      this.addProductError = null;
      this.showAddProductModal = true;

      if (this.supermercados.length === 0) {
        this.loadingSupermercados = true;
        try {
          const response = await apiRequest(API_ENDPOINTS.SUPERMARKET, { method: "GET" });
          const data = await handleApiResponse(response);
          this.supermercados = Array.isArray(data) ? data : (data.data || data.supermercados || []);
        } catch (error) {
          console.error("Erro ao carregar supermercados:", error);
        } finally {
          this.loadingSupermercados = false;
        }
      }
    },

    closeAddProductModal() {
      if (this.savingAddProduct) return;
      this.showAddProductModal = false;
      if (this.addProductImagePreviewUrl) URL.revokeObjectURL(this.addProductImagePreviewUrl);
      this.addProductImageFile = null;
      this.addProductImagePreviewUrl = null;
      this.addProductError = null;
    },

    triggerAddProductImageInput() {
      this.$refs.addProductImageInput.click();
    },

    handleAddProductImageFileChange(event) {
      const file = event.target.files[0];
      if (file) this.setAddProductImageFile(file);
    },

    handleAddProductImageDrop(event) {
      this.addProductImageDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) this.setAddProductImageFile(file);
    },

    setAddProductImageFile(file) {
      if (this.addProductImagePreviewUrl) URL.revokeObjectURL(this.addProductImagePreviewUrl);
      this.addProductImageFile = file;
      this.addProductImagePreviewUrl = URL.createObjectURL(file);
    },

    toggleAddProductSupermarket(id) {
      const next = new Set(this.addProductSupermarketIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      this.addProductSupermarketIds = next;
    },

    async submitAddProduct() {
      if (!this.canSubmitAddProduct) return;

      this.savingAddProduct = true;
      this.addProductError = null;

      const price = parseFloat(this.addProductForm.price);
      const supermarketIds = Array.from(this.addProductSupermarketIds);
      const failedMarkets = [];

      try {
        for (const idSupermarket of supermarketIds) {
          try {
            const formData = new FormData();
            formData.append("name", this.addProductForm.name);
            if (this.addProductForm.brand) formData.append("brand", this.addProductForm.brand);
            if (this.addProductForm.quantity) formData.append("quantity", this.addProductForm.quantity);
            if (this.addProductForm.unit) formData.append("unit", this.addProductForm.unit);
            formData.append("price", price);
            formData.append("id_supermarket", idSupermarket);
            formData.append("id_flyer", this.flyerId);
            if (this.addProductForm.home) formData.append("home", "1");
            if (this.addProductImageFile) formData.append("image", this.addProductImageFile);

            await handleApiResponse(
              await apiRequest(API_ENDPOINTS.FLYER_PRODUCT_CREATE, {
                method: "POST",
                body: formData,
              })
            );
          } catch (err) {
            const market = this.supermercados.find((s) => s.id === idSupermarket);
            failedMarkets.push(market?.nome || market?.name || `#${idSupermarket}`);
            console.error(`Erro ao adicionar produto no supermercado ${idSupermarket}:`, err);
          }
        }

        if (failedMarkets.length === supermarketIds.length) {
          this.addProductError = "Não foi possível adicionar o produto em nenhum supermercado selecionado";
          return;
        }

        this.showAddProductModal = false;
        await this.loadProducts();

        if (failedMarkets.length > 0) {
          showErrorAlert(
            "Adicionado parcialmente",
            `Produto adicionado, mas falhou nos supermercados: ${failedMarkets.join(", ")}`
          );
        } else {
          await showSuccessAlert("Produto adicionado!", "O produto foi adicionado ao folheto com sucesso.", 3000);
        }
      } finally {
        this.savingAddProduct = false;
      }
    },
  },
};
</script>

<style scoped>
.btn-custom-gradient {
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-custom-gradient:hover {
  background: linear-gradient(310deg, #15a8d1 0%, #1bb88a 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(23, 193, 232, 0.3);
}

.spinner-teal {
  color: #20c997 !important;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.product-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.product-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.product-thumb {
  position: relative;
  height: 110px;
  background: linear-gradient(135deg, #f5f7fa 0%, #eef0f4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.product-home-badge {
  position: absolute;
  bottom: 6px;
  left: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  background: linear-gradient(310deg, #fbcf33 0%, #f5a623 100%);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  z-index: 2;
}

.product-edit-image-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  z-index: 2;
}

.product-edit-image-btn:hover {
  background: #17c1e8;
  transform: scale(1.1);
}

.product-delete-btn {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  z-index: 2;
}

.product-delete-btn:hover:not(:disabled) {
  background: #ea0606;
  transform: scale(1.1);
}

.product-delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.edit-image-upload-zone {
  border: 2px dashed #dee2e6;
  border-radius: 0.75rem;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.edit-image-upload-zone:hover {
  border-color: #17c1e8;
  background: rgba(23, 193, 232, 0.04);
}

.edit-image-upload-zone.has-file {
  border-color: #20c997;
  border-style: solid;
  background: rgba(32, 201, 151, 0.04);
  padding: 0.5rem;
}

.edit-image-upload-zone .upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6c757d;
}

.edit-image-upload-zone .upload-placeholder i {
  font-size: 1.8rem;
  color: #17c1e8;
}

.edit-image-preview {
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
  border-radius: 8px;
}

.product-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-thumb--clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.product-thumb--clickable:hover {
  opacity: 0.8;
}

.product-no-img {
  font-size: 2rem;
  color: #ced4da;
}

.product-info {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #344767;
  margin: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.7em;
}

.product-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #8392a5;
}

.product-price {
  font-size: 0.9rem;
  font-weight: 800;
  color: #20c997;
  margin: 0;
}

.product-price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.product-edit-link-btn {
  border: none;
  background: transparent;
  color: #17c1e8;
  font-weight: 700;
  font-size: 0.68rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s ease;
}

.product-edit-link-btn:hover {
  color: #0ea5c7;
}

.product-edit-link-btn--full {
  justify-content: center;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 2px;
}

.modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.image-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}

.image-lightbox-img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  cursor: default;
}

.image-lightbox-close {
  position: absolute;
  top: 24px;
  right: 32px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.image-lightbox-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.supermarket-select-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
}

.supermarket-select-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f7fafc;
  border: 1.5px solid #eef0f4;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  margin: 0;
}

.supermarket-select-row:hover {
  border-color: rgba(23, 193, 232, 0.4);
  background: rgba(23, 193, 232, 0.04);
}

.supermarket-select-row--selected {
  border-color: #17c1e8;
  background: rgba(23, 193, 232, 0.08);
}

.supermarket-select-checkbox {
  accent-color: #17c1e8;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
}
</style>
